import { useRef, useEffect, useCallback } from "react"
import type { Puzzle } from "@shared/types.ts"
import { GridCell } from "./GridCell.tsx"
import { getWordCells, type CellClueInfo } from "../lib/grid-utils.ts"
import { SENTINEL, parseInputValue, tapToCell } from "../lib/mobile-input.ts"

interface CrosswordGridProps {
  puzzle: Puzzle
  cells: Record<string, string>
  currentCell: [number, number] | null
  direction: "across" | "down"
  checkedCells: Record<string, "correct" | "incorrect">
  revealedCells: Set<string>
  flashCells: Record<string, "success" | "error">
  cellClueMap: Map<string, CellClueInfo>
  onCellClick: (row: number, col: number) => void
  onLetterInput?: (letter: string) => void
  onBackspace?: () => void
}

const CELL_SIZE = 36
const PADDING = 1

export function CrosswordGrid({
  puzzle,
  cells,
  currentCell,
  direction,
  checkedCells,
  revealedCells,
  flashCells,
  cellClueMap,
  onCellClick,
  onLetterInput,
  onBackspace,
}: CrosswordGridProps) {
  const size = puzzle.gridSize
  const svgSize = size * CELL_SIZE + PADDING * 2
  const hiddenInputRef = useRef<HTMLInputElement>(null)
  // Track whether we've already handled this input cycle (to avoid double-firing)
  const handledRef = useRef(false)

  // Reset the hidden input to sentinel value
  const resetInput = useCallback(() => {
    if (hiddenInputRef.current) {
      hiddenInputRef.current.value = SENTINEL
    }
  }, [])

  // Focus the hidden input whenever a cell is selected
  useEffect(() => {
    if (currentCell && hiddenInputRef.current) {
      hiddenInputRef.current.focus({ preventScroll: true })
      resetInput()
    }
  }, [currentCell, resetInput])

  const containerRef = useRef<HTMLDivElement>(null)

  // Handle taps on the transparent input overlay — map tap coordinates to grid cells
  const handleInputTap = (e: React.MouseEvent<HTMLInputElement>) => {
    const container = containerRef.current
    if (!container) return
    const svg = container.querySelector("svg")
    if (!svg) return

    const rect = svg.getBoundingClientRect()
    const cell = tapToCell(e.clientX, e.clientY, rect, svgSize, CELL_SIZE, PADDING, size)
    if (cell) {
      onCellClick(cell[0], cell[1])
    }
  }

  // Primary handler: onBeforeInput — works on desktop and some mobile browsers
  const handleBeforeInput = (e: React.FormEvent<HTMLInputElement>) => {
    const event = e.nativeEvent as InputEvent
    if (event.inputType === "deleteContentBackward") {
      e.preventDefault()
      handledRef.current = true
      onBackspace?.()
      resetInput()
    } else if (event.inputType === "insertText" && event.data) {
      e.preventDefault()
      handledRef.current = true
      const letter = event.data.slice(-1)
      if (/^[a-zA-Z]$/.test(letter)) {
        onLetterInput?.(letter.toUpperCase())
      }
      resetInput()
    }
  }

  // Fallback handler: onInput — catches mobile keyboards that skip/ignore beforeinput
  const handleInput = () => {
    // If beforeinput already handled this, skip
    if (handledRef.current) {
      handledRef.current = false
      return
    }
    // Skip during IME composition (compositionend will handle it)
    if (composingRef.current) return

    dispatchFromInputValue()
  }

  // Read the hidden input's value, determine the action, and dispatch
  const dispatchFromInputValue = () => {
    const el = hiddenInputRef.current
    if (!el) return
    const action = parseInputValue(el.value)

    if (action.type === "letter") {
      onLetterInput?.(action.letter)
    } else if (action.type === "backspace") {
      onBackspace?.()
    }
    resetInput()
  }

  // Handle IME composition (Android Gboard, Samsung keyboard, etc.)
  const composingRef = useRef(false)
  const handleCompositionStart = () => {
    composingRef.current = true
  }
  const handleCompositionEnd = () => {
    composingRef.current = false
    // Process whatever the IME committed
    dispatchFromInputValue()
  }

  // Compute highlighted cells (cells in the same word as current cell)
  const highlightedCells = new Set<string>()
  if (currentCell) {
    const wordCells = getWordCells(puzzle, currentCell[0], currentCell[1], direction, cellClueMap)
    for (const [r, c] of wordCells) {
      highlightedCells.add(`${r},${c}`)
    }
  }

  return (
    <div className="grid-container" ref={containerRef} style={{ position: "relative" }}>
      {/* Transparent input overlay — tapping it triggers mobile keyboard directly */}
      <input
        ref={hiddenInputRef}
        inputMode="text"
        onClick={handleInputTap}
        onBeforeInput={handleBeforeInput}
        onInput={handleInput}
        onCompositionStart={handleCompositionStart}
        onCompositionEnd={handleCompositionEnd}
        defaultValue={SENTINEL}
        autoCapitalize="characters"
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        enterKeyHint="next"
        aria-label="Crossword input"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          fontSize: 16,
          color: "transparent",
          background: "transparent",
          caretColor: "transparent",
          border: "none",
          outline: "none",
          padding: 0,
          zIndex: 1,
          // Prevent mobile selection handles and text magnifier
          userSelect: "none",
          WebkitUserSelect: "none",
        }}
      />
      <svg
        viewBox={`0 0 ${svgSize} ${svgSize}`}
        style={{ outline: "none", display: "block" }}
      >
        <rect x={0} y={0} width={svgSize} height={svgSize} fill="#12121f" />
        <g transform={`translate(${PADDING}, ${PADDING})`}>
          {puzzle.grid.map((row, r) =>
            row.map((cell, c) => {
              const key = `${r},${c}`
              const isActive = currentCell !== null && currentCell[0] === r && currentCell[1] === c
              const isHighlighted = highlightedCells.has(key)

              return (
                <GridCell
                  key={key}
                  cell={cell}
                  cellSize={CELL_SIZE}
                  userLetter={cells[key]}
                  isActive={isActive}
                  isHighlighted={isHighlighted}
                  checkStatus={checkedCells[key]}
                  flashStatus={flashCells[key]}
                  isRevealed={revealedCells.has(key)}
                />
              )
            }),
          )}
        </g>
      </svg>
    </div>
  )
}
